/**
 * Company Profile Fields Library — types, helpers and seed data.
 *
 * Each field is defined once at the org level (label, format, options). Company
 * Groups compose their forms from these definitions, so a field stays consistent
 * everywhere it is used. Format is fixed once a field is created.
 *
 * NOTE: This first cut is backed by local/mock state — there is no backend
 * endpoint yet. `usedIn` (number of groups) and `optionUsage` (companies per
 * dropdown option) are seeded so the "in use" warnings behave realistically.
 */

export type FieldType =
  | "text"
  | "number"
  | "phone"
  | "email"
  | "dropdown_single"
  | "dropdown_multi"
  | "yes_no"
  | "date";

export type LibraryField = {
  id: string;
  key: string;
  label: string;
  type: FieldType;
  system: boolean;
  /** Built-in hardcoded company field (Company Name, ID, Primary Contact, …) — a
   * stricter, non-composable tier of `system`. Read-only and always captured. */
  builtIn: boolean;
  options: string[];
  deprecated: boolean;
  defaultRequired: boolean;
  /** Mock: number of Company Groups that compose this field. */
  usedIn: number;
  /** Mock: how many companies have each dropdown option selected. */
  optionUsage?: Record<string, number>;
};

/** All formats with their display labels. */
export const TYPE_LABELS: Record<FieldType, string> = {
  text: "Text",
  number: "Number",
  phone: "Phone",
  email: "Email",
  dropdown_single: "Dropdown (single)",
  dropdown_multi: "Dropdown (multi)",
  yes_no: "Yes / No",
  date: "Date",
};

/** Formats a user may pick when creating a custom field. number/phone/email are reserved for system fields. */
export const CUSTOM_TYPES: { value: FieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "dropdown_single", label: "Dropdown (single)" },
  { value: "dropdown_multi", label: "Dropdown (multi)" },
  { value: "yes_no", label: "Yes / No" },
  { value: "date", label: "Date" },
];

export const typeLabel = (t: FieldType): string => TYPE_LABELS[t] || "Text";

export const isDropdown = (t: FieldType): boolean =>
  t === "dropdown_single" || t === "dropdown_multi";

/** Derive a unique snake_case key from a label, avoiding collisions with existing keys. */
export const keyFromLabel = (label: string, existingKeys: string[]): string => {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "field";
  const taken = new Set(existingKeys);
  let key = base;
  let i = 1;
  while (taken.has(key)) {
    key = `${base}_${++i}`;
  }
  return key;
};

let _uid = 100;
export const uid = (prefix: string): string => `${prefix}_${++_uid}`;

/**
 * The library the UI renders = system fields (code-owned, same for every tenant)
 * + custom fields (per tenant, from the DB). Both the definitions and the merge
 * live in ./systemProfileFields — see that file for the source-of-truth rationale
 * and the `GET /profile-fields` contract.
 */
export {
  SYSTEM_PROFILE_FIELDS,
  mergeProfileFields,
  getProfileFields,
  saveCustomFields,
  deleteCustomField,
  initialLibrary,
} from "./systemProfileFields";
export type { ProfileFieldDef, ProfileFieldUsage } from "./systemProfileFields";
